import * as React from 'react'
import {
  Container,
  BackgroundImage,
  Darker,
  Box,
  Logo,
  InputsContainer,
  Buttons,
  BoxHide,
  HideColor,
  Inputs,
  ErrorMsg,
  AnimatedLoginAvatar,
} from './styled'
import { Spinner } from '../../components/Spinner'
import { images, color } from '../../theme'
import { Input } from '../../components/Input'
import { produce } from 'immer'
import { Button } from '../../components/Button'
import { API, ApiResponse } from '../../services/api'
import { inject, observer } from 'mobx-react'
import { UserStore } from '../../stores/UserModel'
import i18next from 'i18next'
import { History } from 'history'
import { USER_STORE } from '../../stores'
import { API_URL } from '../../constants'

enum INPUTS {
  LOGIN = 'LOGIN',
  PASSWORD = 'PASSWORD',
  EMAIL = 'EMAIL',
  REPEAT_PASSWORD = 'REPEAT_PASSWORD',
  FIRST_NAME = 'FIRST_NAME',
}

interface RegistryPageProps {
  isServer: boolean
  [USER_STORE]?: UserStore
  history: History
}

interface InputData {
  value: string
  isError: boolean
}

interface RegistryPageState {
  [INPUTS.LOGIN]: InputData
  [INPUTS.PASSWORD]: InputData
  isLoading: boolean
  isLogged: boolean
  error: string
}
const EMPTY_STRING = ''

@inject(USER_STORE)
@observer
class RegistryPage extends React.Component<RegistryPageProps, RegistryPageState> {
  private API = API.getInstance()

  state = {
    [INPUTS.LOGIN]: {
      value: EMPTY_STRING,
      isError: false,
    },
    [INPUTS.PASSWORD]: {
      value: EMPTY_STRING,
      isError: false,
    },
    [INPUTS.EMAIL]: {
      value: EMPTY_STRING,
      isError: false,
    },
    [INPUTS.REPEAT_PASSWORD]: {
      value: EMPTY_STRING,
      isError: false,
    },
    [INPUTS.FIRST_NAME]: {
      value: EMPTY_STRING,
      isError: false,
    },
    isLoading: true,
    isLogged: false,
    error: '',
  }

  constructor(props: RegistryPageProps) {
    super(props)
    if (props[USER_STORE].isLogged()) {
      props.history.replace('/')
    } else {
      this.state.isLoading = false
    }
  }

  navigateToDashboard = () => {
    setTimeout(() =>
      this.setState({ isLogged: true }, () =>
        setTimeout(() => this.props.history.replace('/'), 1000)
      )
    )
  }

  handleValueChange = (input: INPUTS) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    this.setState(
      produce((draft) => {
        draft[input].value = value
        draft[input].isError = false
      })
    )
  }

  handleRegistry = async () => {
    const {
      [INPUTS.LOGIN]: login,
      [INPUTS.PASSWORD]: password,
      [INPUTS.EMAIL]: email,
      [INPUTS.REPEAT_PASSWORD]: rePassword,
      [INPUTS.FIRST_NAME]: firstName,
      isLoading,
    } = this.state
    if (isLoading) return
    const isLogin = !login.value
    const isPassword = !password.value
    const isRePassword = !rePassword.value
    const isEmail = !email.value
    if (isPassword || isLogin) {
      return this.setState(
        produce((draft: RegistryPageState) => {
          draft[INPUTS.PASSWORD].isError = isPassword
          draft[INPUTS.LOGIN].isError = isLogin
          draft[INPUTS.REPEAT_PASSWORD].isError = isRePassword
          draft[INPUTS.EMAIL].isError = isEmail
          draft.error = i18next.t('registryPage.error.withoutRequiredData')
        })
      )
    }
    if (password.value !== rePassword.value) {
      return this.setState(
        produce((draft: RegistryPageState) => {
          draft[INPUTS.PASSWORD].isError = true
          draft[INPUTS.PASSWORD].value = ''
          draft[INPUTS.REPEAT_PASSWORD].isError = true
          draft[INPUTS.REPEAT_PASSWORD].value = ''
          draft.error = i18next.t('registryPage.error.passwordsShouldEquals')
        })
      )
    }
    if (password.value.length < 8) {
      return this.setState(
        produce((draft: RegistryPageState) => {
          draft[INPUTS.PASSWORD].isError = true
          draft[INPUTS.PASSWORD].value = ''
          draft[INPUTS.REPEAT_PASSWORD].isError = true
          draft[INPUTS.REPEAT_PASSWORD].value = ''
          draft.error = i18next.t('registryPage.error.toShortPassword')
        })
      )
    }
    this.setState({ isLoading: true })
    let response: ApiResponse<any>
    response = await this.API.register({
      username: login.value,
      password: password.value,
      email: email.value,
      firstName: firstName.value,
    })
    const { status, data: { code = 400, ...data } = {} } = response
    if (status === 200) {
      this.props[USER_STORE].setUserData(data)
      this.navigateToDashboard()
    } else {
      console.warn(data)
      let error: string
      switch (code) {
        case 1: {
          error = i18next.t('registryPage.error.withoutRequiredData')
          break
        }
        case 11000: {
          error = i18next.t('registryPage.error.duplicatedUsernameOrMail')
          break
        }
        default: {
          if (data.errors) {
            error = data.errors[0].msg
          } else {
            error = i18next.t('registryPage.error.unexpected')
          }
          break
        }
      }
      setTimeout(() => {
        this.setState(
          produce((draft: RegistryPageState) => {
            draft.isLoading = false
            draft.error = error
            draft[INPUTS.PASSWORD].value = ''
            draft[INPUTS.REPEAT_PASSWORD].value = ''
          })
        )
      }, 1000)
    }
  }

  handleSubmit = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') await this.handleRegistry()
  }

  handleLogin = () => {
    this.props.history.push('/login')
  }

  render() {
    const { first_name } = this.props[USER_STORE]
    const {
      [INPUTS.LOGIN]: login,
      [INPUTS.PASSWORD]: password,
      [INPUTS.EMAIL]: email,
      [INPUTS.REPEAT_PASSWORD]: rePassword,
      [INPUTS.FIRST_NAME]: firstName,
      isLoading,
      error,
      isLogged,
    } = this.state
    return (
      <Container>
        <BackgroundImage image={`${API_URL}/cover`}>
          <Darker>
            <Box>
              {isLogged ? (
                <AnimatedLoginAvatar
                  helloText={i18next.t('registryPage.hello', { name: first_name })}
                  user={this.props[USER_STORE]}
                />
              ) : (
                <BoxHide size={isLoading ? 50 : 600}>
                  <InputsContainer>
                    {isLoading && (
                      <HideColor>
                        <Spinner size="lg" />
                      </HideColor>
                    )}

                    <Inputs>
                      <Logo src={images.logo} />
                      <Input
                        onKeyPress={this.handleSubmit}
                        placeholder={i18next.t('registryPage.username')}
                        isError={login.isError}
                        value={login.value}
                        onChange={this.handleValueChange(INPUTS.LOGIN)}
                      />
                      <Input
                        onKeyPress={this.handleSubmit}
                        placeholder={i18next.t('registryPage.firstName')}
                        isError={firstName.isError}
                        value={firstName.value}
                        onChange={this.handleValueChange(INPUTS.FIRST_NAME)}
                      />
                      <Input
                        onKeyPress={this.handleSubmit}
                        isError={email.isError}
                        value={email.value}
                        placeholder={i18next.t('registryPage.email')}
                        onChange={this.handleValueChange(INPUTS.EMAIL)}
                      />
                      <Input
                        onKeyPress={this.handleSubmit}
                        type="password"
                        isError={password.isError}
                        value={password.value}
                        placeholder={i18next.t('registryPage.password')}
                        onChange={this.handleValueChange(INPUTS.PASSWORD)}
                      />
                      <Input
                        onKeyPress={this.handleSubmit}
                        type="password"
                        isError={rePassword.isError}
                        value={rePassword.value}
                        placeholder={i18next.t('registryPage.repeatPassword')}
                        onChange={this.handleValueChange(INPUTS.REPEAT_PASSWORD)}
                      />
                      <Buttons>
                        <Button
                          onClick={this.handleLogin}
                          margin="0 5px 0 0"
                          flex
                          label={i18next.t('loginPage.logIn')}
                        />
                        <Button
                          onClick={this.handleRegistry}
                          activeBacgroundColor={color.whiteBlue}
                          margin="0 0 0 5px"
                          activeColor={color.darkBlue}
                          backgroundColor={color.redMain}
                          flex
                          label={i18next.t('registryPage.registry')}
                        />
                      </Buttons>
                      {Boolean(error) && <ErrorMsg>{error}</ErrorMsg>}
                    </Inputs>
                  </InputsContainer>
                </BoxHide>
              )}
            </Box>
          </Darker>
        </BackgroundImage>
      </Container>
    )
  }
}

export default RegistryPage
